import type { PortalProjectDetail, ProjectTask, Appointment, MessageNotification } from '@altios/api-client';

export type PortalView = 'home' | 'selections' | 'money' | 'messages' | 'schedule' | 'files' | 'progress' | 'more';
export type PortalMoneySection = 'overview' | 'estimates' | 'agreements' | 'changes' | 'payments';

export const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function formatMoney(value?: number | null): string {
  return money.format(Number(value || 0));
}

export function formatDate(value?: string | null, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, options || { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function phaseTone(phase?: string | null): 'violet' | 'indigo' | 'blue' | 'cyan' | 'teal' | 'emerald' | 'lime' | 'amber' | 'orange' | 'rose' | 'slate' {
  const value = String(phase || '').toLowerCase();
  if (value.includes('lead') || value.includes('discovery')) return 'violet';
  if (value.includes('design')) return 'indigo';
  if (value.includes('estimate')) return 'blue';
  if (value.includes('preconstruction') || value.includes('pre-construction') || value.includes('prep')) return 'cyan';
  if (value.includes('selection')) return 'teal';
  if (value.includes('contract') || value.includes('agreement')) return 'emerald';
  if (value.includes('procure')) return 'lime';
  if (value.includes('project build') || value === 'build') return 'amber';
  if (value.includes('substantial') || value.includes('punch')) return 'orange';
  if (value.includes('closeout') || value.includes('finishing')) return 'rose';
  return 'slate';
}

export function currentPhase(detail: PortalProjectDetail): string {
  return String(detail.project.phase || detail.project.current_phase || detail.project.stage || 'Project');
}

export function customerTask(task: ProjectTask): boolean {
  return task.assigned_to === 'Customer' || task.assigned_to === 'Both';
}

export function taskDone(task: ProjectTask): boolean {
  return String(task.status || '').toLowerCase() === 'done';
}

export function taskTitle(task: ProjectTask): string {
  return String(task.customer_title || task.title || 'Project item');
}

export function taskDate(task: ProjectTask): Date | null {
  const raw = task.start_date || task.due_date || task.end_date;
  if (!raw) return null;
  const date = new Date(String(raw).length === 10 ? `${raw}T12:00:00` : raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function appointmentDate(appointment: Appointment): Date | null {
  if (!appointment.starts_at) return null;
  const date = new Date(appointment.starts_at);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function selectionComplete(selection: PortalProjectDetail['selections'][number]): boolean {
  const flag = (value: unknown, fallback = false) => value === undefined || value === null ? fallback : value === true || Number(value || 0) === 1;
  if (flag(selection.staff_incomplete_override)) return false;
  if (flag(selection.altios_choose)) return true;

  if (flag(selection.needs_product_code, true)) {
    const productComplete = Boolean(
      selection.resulting_lineage_id ||
      selection.chosen_option_id ||
      (selection.customer_product_description && (selection.customer_product_code || selection.customer_product_url))
    );
    if (!productComplete) return false;
  }
  if (flag(selection.needs_finish_choice) && !String(selection.customer_finish_choice || '').trim()) return false;
  if (flag(selection.needs_layout_choice) && !String(selection.customer_layout_choice || '').trim()) return false;
  if (flag(selection.needs_placement_choice) && !String(selection.customer_placement_choice || '').trim()) return false;
  if (flag(selection.needs_dimensions) && !String(selection.customer_dimensions || '').trim()) return false;
  return true;
}

export function notificationTime(item: MessageNotification): number {
  const time = Date.parse(String(item.created_at || ''));
  return Number.isFinite(time) ? time : 0;
}

export function humanizeStatus(value?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return 'Not started';
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}
